import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import cn from 'classnames';

import { FREIGHTS } from 'helpers/routes';
import { useSearchLookups } from 'hooks/useSearchLookups';
import { SearchIcon } from 'components/common/Icons';
import styles from './styles.module.scss';

interface QuickSearchBarProps {
  isExpanded: boolean;
  onToggleExpanded: () => void;
  authenticated: boolean;
  hideExpandButton?: boolean;
}

interface AddressValue {
  country_code?: string;
  post_code?: string;
  city?: string;
  country?: string;
  full_address?: string;
}

interface AddressField {
  value?: AddressValue;
  label?: string;
}

interface FormValues {
  loading_from?: string;
  loading_to?: string;
  transport_bodytype_ids?: (number | string)[];
  loading_type_ids?: (number | string)[];
  payment_types_ids?: (number | string)[];
  loading_address?: AddressField | null;
  unloading_address?: AddressField | null;
}

const QuickSearchBar: React.FC<QuickSearchBarProps> = ({
  isExpanded,
  onToggleExpanded,
  authenticated,
  hideExpandButton = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { values } = useFormikContext<FormValues>();
  const { getBodyTypeLabel, getLoadingTypeLabels, getPaymentTypeLabels } = useSearchLookups();

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatLocation = (address?: AddressField | null) => {
    if (!address?.value) return null;
    const { country_code, post_code, city, country } = address.value;
    const parts: string[] = [];
    if (country_code) parts.push(country_code.toUpperCase());
    if (post_code) parts.push(post_code);
    const locationName = city || country;
    if (locationName) parts.push(locationName);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const fromLocation = formatLocation(values.loading_address);
  const toLocation = formatLocation(values.unloading_address);
  const routeText = fromLocation || toLocation
    ? `${fromLocation || '—'} - ${toLocation || '—'}`
    : null;

  const hasDateFilter = values.loading_from || values.loading_to;
  const dateChipText = hasDateFilter
    ? `${t('freightSearch.dateFilter')}: ${formatDate(values.loading_from)} - ${formatDate(values.loading_to)}`
    : null;
  const bodyTypeLabel = getBodyTypeLabel(values.transport_bodytype_ids);
  const loadingTypeLabels = getLoadingTypeLabels(values.loading_type_ids);
  const paymentTypeLabels = getPaymentTypeLabels(values.payment_types_ids);

  const handleAddFreight = () => {
    navigate(FREIGHTS.add);
  };

  // Hide chips when status filter is active (hideExpandButton is true in that case)
  const showChips = !hideExpandButton;

  return (
    <div className={cn(styles.quickSearchBar, { [styles.withTabs]: authenticated })}>
      <div className={styles.leftSection}>
        <span className={styles.label}>{t('freightSearch.currentParams')}:</span>
        {showChips && routeText && (
          <div className={styles.dateChip}>
            <span>{t('freightSearch.route')}: {routeText}</span>
          </div>
        )}
        {showChips && dateChipText && (
          <div className={styles.dateChip}>
            <span>{dateChipText}</span>
          </div>
        )}
        {showChips && bodyTypeLabel && (
          <div className={styles.dateChip}>
            <span>{t('freightSearch.bodyType')}: {bodyTypeLabel}</span>
          </div>
        )}
        {showChips && loadingTypeLabels && (
          <div className={styles.dateChip}>
            <span>{t('freightSearch.loadingType')}: {loadingTypeLabels}</span>
          </div>
        )}
        {showChips && paymentTypeLabels && (
          <div className={styles.dateChip}>
            <span>{t('freightSearch.rateType')}: {paymentTypeLabels}</span>
          </div>
        )}
      </div>
      <div className={styles.rightSection}>
        {!hideExpandButton && (
          <button
            type="button"
            className={styles.expandedSearchBtn}
            onClick={onToggleExpanded}
          >
            <SearchIcon />
            {t('freightSearch.expandedSearch')}
          </button>
        )}
        {authenticated && (
          <button
            type="button"
            className={styles.addFreightBtn}
            onClick={handleAddFreight}
          >
            {t('freightSearch.addFreight')}
          </button>
        )}
      </div>
    </div>
  );
};

export default QuickSearchBar;
